import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Input, InputNumber, Modal, Popconfirm, Select, Spin, message } from 'antd';
import {
    AppstoreOutlined,
    DeleteOutlined,
    PlusOutlined,
    ReloadOutlined,
    SaveOutlined,
    SearchOutlined,
    UndoOutlined,
} from '@ant-design/icons';
import { axiosInstance } from '../../config';
import {
    CATALOG_GROUPS,
    catalogFieldLabels,
    catalogTypeMap,
    formatCatalogSummary,
    groupedCatalogOptions,
    isMonthlyType,
    isPercentType,
} from './costCalc';

const emptyDraft = () => ({ name: '', type: 'material', cost: 0, monthly_qty: 1 });

const MonthlyQtyField = ({ type, value, onChange }) => {
    const labels = catalogFieldLabels(type);
    return (
        <div>
            <div className="mb-1 text-xs font-medium text-slate-500">{labels.qty}</div>
            <InputNumber
                value={value}
                onChange={onChange}
                min={0.01}
                precision={0}
                decimalSeparator=","
                addonAfter="шт"
                style={{ width: '100%' }}
            />
            <div className="mt-1 text-[11px] text-slate-400">{labels.qtyHint}</div>
        </div>
    );
};

const CatalogModal = ({ open, initial, onCancel, onSave }) => {
    const [draft, setDraft] = useState(emptyDraft);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setDraft(
                initial
                    ? {
                          name: initial.name,
                          type: initial.type,
                          cost: initial.cost,
                          monthly_qty: initial.monthly_qty || 1,
                      }
                    : emptyDraft()
            );
        }
    }, [open, initial]);

    const submit = async () => {
        if (!String(draft.name || '').trim()) {
            message.warning('Укажите название');
            return;
        }
        setSaving(true);
        try {
            await onSave({
                name: String(draft.name).trim(),
                type: draft.type,
                cost: Number(draft.cost) || 0,
                monthly_qty: Number(draft.monthly_qty) > 0 ? Number(draft.monthly_qty) : 1,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            title={initial ? 'Изменить позицию' : 'Новая позиция справочника'}
            okText={initial ? 'Сохранить' : 'Добавить'}
            cancelText="Отмена"
            onCancel={onCancel}
            onOk={submit}
            confirmLoading={saving}
            destroyOnClose
            width={560}
        >
            <div className="space-y-4 pt-2">
                <div>
                    <div className="mb-1 text-xs font-medium text-slate-500">
                        {catalogFieldLabels(draft.type).name}
                    </div>
                    <Input
                        autoFocus
                        value={draft.name}
                        onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                        onPressEnter={submit}
                        placeholder={catalogFieldLabels(draft.type).placeholder}
                    />
                </div>
                <div>
                    <div className="mb-2 text-xs font-medium text-slate-500">Тип</div>
                    <div className="space-y-2">
                        {CATALOG_GROUPS.map((group) => (
                            <div key={group.id} className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                    {group.label}
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {group.types.map((typeId) => {
                                        const type = catalogTypeMap[typeId];
                                        const selected = draft.type === typeId;
                                        return (
                                            <button
                                                key={typeId}
                                                type="button"
                                                onClick={() =>
                                                    setDraft((prev) => ({
                                                        ...prev,
                                                        type: typeId,
                                                        monthly_qty:
                                                            typeId === prev.type ? prev.monthly_qty : 1,
                                                    }))
                                                }
                                                className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                                                    selected
                                                        ? `${type.accent} ring-1 ring-current`
                                                        : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-slate-300'
                                                }`}
                                            >
                                                {type.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div>
                    <div className="mb-1 text-xs font-medium text-slate-500">
                        {catalogFieldLabels(draft.type).cost}
                    </div>
                    <InputNumber
                        value={draft.cost}
                        onChange={(cost) => setDraft((prev) => ({ ...prev, cost }))}
                        min={0}
                        max={isPercentType(draft.type) ? 100 : undefined}
                        precision={2}
                        decimalSeparator=","
                        addonAfter={isPercentType(draft.type) ? '%' : '₽'}
                        style={{ width: '100%' }}
                    />
                </div>
                {isMonthlyType(draft.type) && (
                    <MonthlyQtyField
                        type={draft.type}
                        value={draft.monthly_qty}
                        onChange={(monthly_qty) => setDraft((prev) => ({ ...prev, monthly_qty }))}
                    />
                )}
            </div>
        </Modal>
    );
};

const CostMaterials = () => {
    const [items, setItems] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [loading, setLoading] = useState(false);
    const [savingIds, setSavingIds] = useState(() => new Set());
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [modal, setModal] = useState({ open: false, item: null });

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/v1/cost/catalog');
            setItems(response.data || []);
            setDrafts({});
        } catch (err) {
            console.error('Error fetching cost catalog:', err);
            message.error('Не удалось загрузить справочник');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const visible = useMemo(() => {
        const query = search.trim().toLowerCase();
        return items.filter((item) => {
            if (typeFilter !== 'all') {
                const group = CATALOG_GROUPS.find((entry) => entry.id === typeFilter);
                if (group ? !group.types.includes(item.type) : item.type !== typeFilter) return false;
            }
            if (!query) return true;
            const draft = drafts[item.id] || item;
            return `${draft.name} ${catalogTypeMap[draft.type]?.label || ''}`.toLowerCase().includes(query);
        });
    }, [items, drafts, search, typeFilter]);

    const grouped = useMemo(
        () =>
            CATALOG_GROUPS.map((group) => ({
                ...group,
                sections: group.types
                    .map((typeId) => ({
                        typeId,
                        ...catalogTypeMap[typeId],
                        items: visible.filter((item) => (drafts[item.id] || item).type === typeId),
                    }))
                    .filter((section) => section.items.length > 0),
            })).filter((group) => group.sections.length > 0),
        [visible, drafts]
    );

    const isDirty = (item) => {
        const draft = drafts[item.id];
        if (!draft) return false;
        return (
            String(draft.name || '').trim() !== String(item.name || '').trim() ||
            draft.type !== item.type ||
            Number(draft.cost || 0) !== Number(item.cost || 0) ||
            Number(draft.monthly_qty || 1) !== Number(item.monthly_qty || 1)
        );
    };

    const dirtyCount = items.filter(isDirty).length;

    const setDraft = (item, patch) =>
        setDrafts((prev) => ({
            ...prev,
            [item.id]: { ...(prev[item.id] || item), ...patch },
        }));

    const handleSave = async (item) => {
        const draft = drafts[item.id] || item;
        const payload = {
            name: String(draft.name || '').trim(),
            type: draft.type,
            cost: Number(draft.cost) || 0,
            monthly_qty: Number(draft.monthly_qty) > 0 ? Number(draft.monthly_qty) : 1,
        };
        if (!payload.name) {
            message.warning('Укажите название');
            return;
        }
        setSavingIds((prev) => new Set(prev).add(item.id));
        try {
            const response = await axiosInstance.put(`/v1/cost/catalog/${item.id}`, payload);
            setItems((prev) => prev.map((row) => (row.id === item.id ? response.data : row)));
            setDrafts((prev) => {
                const next = { ...prev };
                delete next[item.id];
                return next;
            });
            message.success(`«${payload.name}» сохранено`);
        } catch (err) {
            console.error('Error saving catalog item:', err);
            message.error(err.response?.data?.detail || 'Не удалось сохранить');
        } finally {
            setSavingIds((prev) => {
                const next = new Set(prev);
                next.delete(item.id);
                return next;
            });
        }
    };

    const handleCreate = async (payload) => {
        const response = await axiosInstance.post('/v1/cost/catalog', payload);
        setItems((prev) => [...prev, response.data]);
        setModal({ open: false, item: null });
        message.success(`«${payload.name}» добавлено`);
    };

    const handleDelete = async (item) => {
        try {
            await axiosInstance.delete(`/v1/cost/catalog/${item.id}`);
            setItems((prev) => prev.filter((row) => row.id !== item.id));
            setDrafts((prev) => {
                const next = { ...prev };
                delete next[item.id];
                return next;
            });
            message.success(`«${item.name}» удалено`);
        } catch (err) {
            console.error('Error deleting catalog item:', err);
            message.error(err.response?.data?.detail || 'Не удалось удалить');
        }
    };

    return (
        <div className="mx-auto max-w-[1400px] space-y-4">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-sky-900 p-6 text-white shadow-lg">
                <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-sky-500/20 blur-3xl" />
                <div className="relative flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-sky-200">
                            <AppstoreOutlined /> Справочник себестоимости
                        </div>
                        <h1 className="mt-1 text-2xl font-semibold text-white">Материалы</h1>
                        <p className="mt-1 max-w-xl text-sm text-slate-300">
                            Название, тип и цена. В карточке артикула позиция выбирается из списка — стоимость
                            подставляется автоматически.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button icon={<ReloadOutlined />} onClick={fetchItems} loading={loading} ghost>
                            Обновить
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setModal({ open: true, item: null })}
                        >
                            Добавить
                        </Button>
                    </div>
                </div>
                <div className="relative mt-6 grid gap-3 sm:grid-cols-3">
                    {CATALOG_GROUPS.map((group) => (
                        <button
                            key={group.id}
                            type="button"
                            onClick={() => setTypeFilter((prev) => (prev === group.id ? 'all' : group.id))}
                            className={`rounded-2xl p-3 text-left backdrop-blur transition-colors ${
                                typeFilter === group.id
                                    ? 'bg-white/25 ring-1 ring-white/40'
                                    : 'bg-white/10 hover:bg-white/15'
                            }`}
                        >
                            <div className="text-[11px] uppercase tracking-wide text-slate-300">{group.label}</div>
                            <div className="mt-1 text-xl font-semibold">
                                {items.filter((item) => group.types.includes(item.type)).length}
                            </div>
                            <div className="mt-1 text-[11px] text-slate-400">
                                {group.types.map((typeId) => catalogTypeMap[typeId]?.label).join(' · ')}
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Input
                    allowClear
                    prefix={<SearchOutlined className="text-slate-400" />}
                    placeholder="Поиск по названию"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    style={{ maxWidth: 360 }}
                />
                {dirtyCount > 0 && (
                    <span className="text-sm text-indigo-600">Не сохранено: {dirtyCount}</span>
                )}
            </div>

            <Spin spinning={loading}>
                {items.length === 0 && !loading ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-12">
                        <Empty description="Добавьте материалы, услуги и фурнитуру, чтобы выбирать их в карточке артикула">
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setModal({ open: true, item: null })}
                            >
                                Добавить первую позицию
                            </Button>
                        </Empty>
                    </div>
                ) : (
                    visible.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-12">
                        <Empty description="Ничего не найдено" />
                    </div>
                    ) : (
                    <div className="space-y-8">
                        {grouped.map((group) => (
                            <section key={group.id} className="space-y-5">
                                <div className="flex items-center gap-2">
                                    <span className="rounded-full bg-slate-900 px-2.5 py-1 text-xs font-semibold text-white">
                                        {group.label}
                                    </span>
                                    <span className="text-xs text-slate-400">
                                        {group.sections.reduce((sum, section) => sum + section.items.length, 0)}
                                    </span>
                                </div>
                                {group.sections.map((section) => (
                                    <div key={section.typeId}>
                                        <div className="mb-3 flex items-center gap-2">
                                            <span
                                                className={`rounded-full px-2.5 py-1 text-xs font-semibold ${section.accent}`}
                                            >
                                                {section.label}
                                            </span>
                                            <span className="text-xs text-slate-400">{section.items.length}</span>
                                        </div>
                                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                                            {section.items.map((item) => {
                                                const value = drafts[item.id] || item;
                                                const dirty = isDirty(item);
                                                return (
                                                    <div
                                                        key={item.id}
                                                        className={`rounded-2xl border bg-white p-4 shadow-sm ${
                                                            dirty
                                                                ? 'border-indigo-300 ring-1 ring-indigo-200'
                                                                : 'border-slate-200'
                                                        }`}
                                                    >
                                                        <div className="mb-3">
                                                            <Input
                                                                value={value.name}
                                                                onChange={(event) =>
                                                                    setDraft(item, { name: event.target.value })
                                                                }
                                                                placeholder={catalogFieldLabels(value.type).name}
                                                            />
                                                        </div>
                                                        <div className="mb-3 grid grid-cols-2 gap-2">
                                                            <Select
                                                                value={value.type}
                                                                onChange={(type) => setDraft(item, { type })}
                                                                options={groupedCatalogOptions}
                                                            />
                                                            <InputNumber
                                                                value={value.cost}
                                                                onChange={(cost) => setDraft(item, { cost })}
                                                                min={0}
                                                                max={isPercentType(value.type) ? 100 : undefined}
                                                                precision={2}
                                                                decimalSeparator=","
                                                                addonAfter={isPercentType(value.type) ? '%' : '₽'}
                                                                style={{ width: '100%' }}
                                                            />
                                                        </div>
                                                        {isMonthlyType(value.type) && (
                                                            <div className="mb-3">
                                                                <MonthlyQtyField
                                                                    type={value.type}
                                                                    value={value.monthly_qty || 1}
                                                                    onChange={(monthly_qty) =>
                                                                        setDraft(item, { monthly_qty })
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="flex items-center justify-between gap-2">
                                                            <span className="text-xs text-slate-400">
                                                                {formatCatalogSummary(value)}
                                                                {dirty && (
                                                                    <span className="ml-2 font-medium text-indigo-500">
                                                                        ● не сохранено
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <div className="flex gap-1">
                                                                {dirty && (
                                                                    <>
                                                                        <Button
                                                                            icon={<UndoOutlined />}
                                                                            onClick={() =>
                                                                                setDrafts((prev) => {
                                                                                    const next = { ...prev };
                                                                                    delete next[item.id];
                                                                                    return next;
                                                                                })
                                                                            }
                                                                        />
                                                                        <Button
                                                                            type="primary"
                                                                            icon={<SaveOutlined />}
                                                                            loading={savingIds.has(item.id)}
                                                                            onClick={() => handleSave(item)}
                                                                        />
                                                                    </>
                                                                )}
                                                                <Popconfirm
                                                                    title={`Удалить «${item.name}»?`}
                                                                    description="В карточках артикулов эта позиция пропадёт из списка"
                                                                    okText="Удалить"
                                                                    cancelText="Отмена"
                                                                    okButtonProps={{ danger: true }}
                                                                    onConfirm={() => handleDelete(item)}
                                                                >
                                                                    <Button
                                                                        type="text"
                                                                        danger
                                                                        icon={<DeleteOutlined />}
                                                                    />
                                                                </Popconfirm>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ))}
                            </section>
                        ))}
                    </div>
                    )
                )}
            </Spin>

            <CatalogModal
                open={modal.open}
                initial={modal.item}
                onCancel={() => setModal({ open: false, item: null })}
                onSave={handleCreate}
            />
        </div>
    );
};

export default CostMaterials;
